import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';
import { isInBetaEnvironment } from './config/features';
import { getBasename } from './config/features';
import { routePreloads } from './routes';
import { hasShareParams } from './utils/shareParams';
import reportWebVitals from './utils/webVitals';

const appContent = (
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter basename={getBasename()}>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);

const rootElement = document.getElementById('root');

// If pre-rendered by react-snap, hydrate instead of render. The current
// route's lazy chunk must finish loading FIRST — react-snap HTML has no
// Suspense dehydration markers, so hydrating into an unloaded lazy route
// makes React discard the server HTML with hydration errors (#418/#423).
// The static HTML stays visible while the chunk loads.
// Paths react-snap prerendered with no lazy chunk (static components).
const STATIC_PRERENDERED = new Set(['/', '/404.html']);

const path = window.location.pathname.replace(/\/+$/, '') || '/';
const isPrerenderedPath = STATIC_PRERENDERED.has(path) || Boolean(routePreloads[path]);

// Hydrate only URLs that were actually prerendered. Anything else (unknown
// URLs) is served the SPA-fallback landing HTML by the host — hydrating that
// against the NotFound tree can only mismatch, so render fresh instead.
// Share links (?c=…&r=…) seed calculator state from the query string, so
// their first render can't match the default-state prerendered HTML. Render
// fresh instead (root.render replaces the static markup); the route chunk is
// still preloaded so the page doesn't flash a Suspense fallback.
const restoringShare = hasShareParams();

if (rootElement.hasChildNodes() && isPrerenderedPath && !restoringShare) {
  const preload = routePreloads[path];
  const hydrate = () =>
    ReactDOM.hydrateRoot(rootElement, appContent, {
      // react-snap serializes post-effect DOM (e.g. auto-computed results),
      // so React's first render can differ in spots it recovers from
      // automatically. Log as warning, not error — the recovery is expected.
      onRecoverableError: (error, errorInfo) => {
        console.warn('[hydration recovered]', error?.message || error, errorInfo?.componentStack || '');
      },
    });
  if (preload) {
    preload().then(hydrate, hydrate);
  } else {
    hydrate();
  }
} else {
  const root = ReactDOM.createRoot(rootElement);
  const render = () => root.render(appContent);
  const preload = restoringShare ? routePreloads[path] : undefined;
  if (preload) {
    preload().then(render, render);
  } else {
    render();
  }
}

// Report Core Web Vitals (LCP, FID/INP, CLS, FCP, TTFB) to Google Analytics
reportWebVitals();

// Register service worker for offline support & PWA install (production only)
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    // Whether this page load was already controlled by a service worker.
    // On a first visit this is false — and must stay false inside the
    // updatefound handler. Checking navigator.serviceWorker.controller at
    // activation time instead made every FIRST visit reload mid-view,
    // because the fresh worker claims the page and becomes the controller.
    const hadController = Boolean(navigator.serviceWorker.controller);
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated' && hadController) {
            window.location.reload();
          }
        });
      });
    }).catch(() => {
      // Service worker registration failed — app still works normally
    });
  });
} else if ('serviceWorker' in navigator && process.env.NODE_ENV !== 'production') {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((reg) => reg.unregister());
  });
}
